import { useContext, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
} from '@mui/material';
import MealPlansContext from '../../context/meal-plans-context';

interface PlanPickerProps {
  mealId: string;
  open: boolean;
  onClose: () => void;
}

const PlanPicker = ({ mealId, open, onClose }: PlanPickerProps) => {
  const { plans, addMealToPlan, createPlan } = useContext(MealPlansContext);
  const [creating, setCreating] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanDate, setNewPlanDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const activePlans = plans.filter((p) => p.status === 'active');

  const handleSelectPlan = async (planId: string) => {
    setSaving(true);
    setError(null);
    try {
      await addMealToPlan(planId, mealId);
      setSuccess('Jídlo bylo přidáno do plánu');
      setTimeout(() => {
        setSuccess(null);
        onClose();
      }, 1000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAndAdd = async () => {
    if (!newPlanName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const plan = await createPlan(newPlanName.trim(), newPlanDate || undefined);
      await addMealToPlan(plan.id, mealId);
      setSuccess('Plán vytvořen a jídlo přidáno');
      setTimeout(() => {
        setSuccess(null);
        setCreating(false);
        setNewPlanName('');
        setNewPlanDate('');
        onClose();
      }, 1000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setCreating(false);
    setNewPlanName('');
    setNewPlanDate('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Přidat do plánu</DialogTitle>
      <DialogContent>
        {success && (
          <Alert severity="success" sx={{ mb: 1 }}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {!creating && (
          <>
            {activePlans.length > 0 ? (
              <List>
                {activePlans.map((plan) => (
                  <ListItemButton key={plan.id} onClick={() => handleSelectPlan(plan.id)} disabled={saving}>
                    <ListItemText
                      primary={plan.name}
                      secondary={plan.dueDate ? `Termín: ${plan.dueDate.toLocaleDateString('cs-CZ')}` : 'Bez termínu'}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Alert severity="info" sx={{ mb: 1 }}>
                Nemáte žádné aktivní plány
              </Alert>
            )}
            <Divider sx={{ my: 1 }} />
            <Button fullWidth onClick={() => setCreating(true)} disabled={saving}>
              Vytvořit nový plán
            </Button>
          </>
        )}

        {creating && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Název plánu"
              value={newPlanName}
              onChange={(e) => setNewPlanName(e.target.value)}
              fullWidth
              size="small"
              autoFocus
            />
            <TextField
              label="Termín (volitelné)"
              type="date"
              value={newPlanDate}
              onChange={(e) => setNewPlanDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Zrušit
        </Button>
        {creating && (
          <Button onClick={handleCreateAndAdd} variant="contained" disabled={saving || !newPlanName.trim()}>
            {saving ? 'Ukládám...' : 'Vytvořit a přidat'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PlanPicker;
